// ============================================================
// DATA FACTORIES - Pure functions for creating data objects
// ============================================================
import type { Slot, Target, Tab, BodyTypeId, TurnTrackerState, TurnParticipant } from '../types';
import type { Character } from './character/types';
import { emptyCombatInstance } from './character/factories';
import { resolveDefenderStats, resolveCombatWeapon, resolveAttackerStats } from './character/combatStatsResolver';
import { defaultArmourForBody } from './bodyConfig';
import deepClone from '../utils/deepClone';

export function emptySlot(index: number): Slot {
  return {
    id: index,
    name: '',
    weaponName: '',
    weaponType: 'ranged',
    damageExpression: '1d10+5',
    damageType: 'X',
    penetration: 4,
    range: 100,
    rateOfFire: { single: true, semi: 3, full: 0 },
    skillValue: 45,
    strengthBonus: 0,
    righteousFury: false,
    canSwiftAttack: false,
    canLightningAttack: false,
    specialRules: [],
    psyRating: 0,
    ammo: { current: 28, max: 28, reloadAction: 'Full' },
    attackMod: 0,
    coneLength: 0,
    isEmpty: true,
  };
}

// Declarative: one line per Target field, the whole default record in one literal.
export function emptyTarget(bodyType: BodyTypeId = 'humanoid'): Target {
  return {
    name: 'New Target',
    wounds: { current: 15, max: 15 },
    toughnessBonus: 3,
    strengthBonus: 3,
    willpowerBonus: 3,
    unnaturalToughness: 1,
    unnaturalAgility: 1,
    unnaturalWeaponSkill: 1,
    unnaturalStrength: 1,
    unnaturalWillpower: 1,
    armour: defaultArmourForBody(bodyType),
    dodge: { canDodge: false, agility: 30, additionalDodges: 0 },
    parry: { canParry: false, weaponSkill: 30, hasPowerField: false, additionalParries: 0, canParryRanged: false },
    traits: [],
    conditions: [],
    criticalDamage: [],
    size: 'Average',
    forceField: { active: false, protectionRating: 0, overloadThreshold: 0 },
    trueGrit: false,
    stuffOfNightmares: false,
    astartes: false,
    xenos: false,
    daemonicProtection: 0,
    incorporeal: false,
    fromBeyond: false,
    blind: false,
    machineAP: 0,
    resistances: [],
    naturalArmour: 0,
    regeneration: 0,
    fearRating: 0,
    psyRating: 0,
    cover: { level: 'none', ap: 0 },
    horde: { isHorde: bodyType === 'horde', magnitude: 30, maxMagnitude: 30 },
    roundCountdown: false,
    // Talent-derived fields (defaults for non-character targets)
    hardy: false,
    ironJaw: false,
    ironJawToughness: 0,
    counterAttackAvailable: false,
    allowFullDefenceChain: false,
    // M8 additions
    defenderCustomMods: [],
    armourOriginal: null,
    // Defender weapon system
    defenderWeapons: [],
    parryBaseSkill: 0,
    // Phase 5: Conditional defence
    defensiveStance: false,
    // Phase 8: Frenzy activation
    frenzied: false,
    // Undying trait
    undying: false,
    // Body type system
    bodyType,
    // AUTO, not front: a new vehicle lets the map say which armour it
    // presents until somebody rules otherwise (v8.52.0).
    vehicleFacing: bodyType === 'vehicle' ? 'auto' : null,
  };
}

/**
 * The scratch rack - what the Armoury shows with no actor selected.
 *
 * Empty, not forty blanks: a rack is exactly as long as it is armed, and
 * "＋ Arm a weapon" is the affordance that grows it (v8.189.0).
 */
export function blankProfile(): Slot[] {
  return [];
}

export function createTab(target: Target, id?: string, sourceCharacter?: Character | null): Tab {
  return {
    id: id || Date.now().toString(),
    label: target.name,
    target: deepClone(target),
    narrativeLog: [],
    sourceCharacter: sourceCharacter ?? null,
  };
}

// ────────────────────────────────────────────────────────────
// Turn Tracker factories
// ────────────────────────────────────────────────────────────

/**
 * A fresh tracker, ON (v8.74.0).
 *
 * It shipped off in v6.6.0 because it was an optional visual aid you
 * opted into. It rolls initiative, orders the fight and ticks the
 * conditions now, so off is the odd choice - and off meant a table
 * discovered none of it unless somebody found the header button.
 *
 * ONLY NEW SESSIONS. An existing session has `active: false` stored in
 * `shared/turnTracker`, and `normalizeTurnTrackerState` leaves a stored
 * value alone; nobody's tracker switches itself on mid-campaign. The
 * header toggle is unchanged and still turns it off.
 */
export function emptyTurnTracker(): TurnTrackerState {
  return {
    active: true,
    round: 1,
    activeIndex: 0,
    participants: [],
    paused: false,
    orderMode: 'initiative',
    joined: [],
  };
}

/**
 * Custom-participant ids, from a counter rather than the clock.
 *
 * `custom-${Date.now()}` collides whenever two are created inside the
 * same millisecond - which a batch admission does every time. Same
 * reasoning as `roster/factories.ts`, and the same reason neither uses
 * `Math.random()`: see gotcha #5.
 */
let customSeq = 0;

export function createParticipant(
  name: string,
  combatantId: string | null,
  isCustom: boolean = false,
): TurnParticipant {
  return {
    // The `tab-` prefix is kept verbatim: participant ids are persisted in
    // shared/turnTracker, and rewriting them would orphan live sessions.
    id: combatantId ? `tab-${combatantId}` : `custom-${Date.now()}-${++customSeq}`,
    name,
    combatantId,
    initiative: null,
    initiativeRoll: null,
    initiativeBonus: null,
    initiativeManual: false,
    status: 'pending',
    countdownTicked: false,
    isCustom,
  };
}

// ────────────────────────────────────────────────────────────
// Character → Target/Slot bridge functions
// ────────────────────────────────────────────────────────────

/**
 * Convert a Character into a Target object with all talent/trait
 * effects resolved. The resulting Target has hardy, ironJaw,
 * additionalDodges, etc. set from the character's talents.
 */
export function characterToTarget(character: Character): Target {
  const instance = emptyCombatInstance(character);
  const stats = resolveDefenderStats(instance);
  return {
    ...stats,
    traits: [],
    defenderCustomMods: [],
    armourOriginal: { ...stats.armour },
  };
}

/**
 * Convert a Character's weapons into an array of Slot-compatible objects.
 * Each weapon is resolved via resolveCombatWeapon (merging character
 * stats/talents), then assigned its position in the rack.
 * Also returns the resolved AttackerCombatStats for damage bonuses.
 *
 * The pre-roster bridge. `projectLoadout` is the live equivalent and a
 * parity test pins the two together, so this densified with it.
 */
export function characterToSlots(character: Character): {
  slots: Slot[];
  stats: ReturnType<typeof resolveAttackerStats> | null;
} {
  const resolvedSlots: Slot[] = [];
  let stats = null;

  for (let i = 0; i < character.weapons.length; i++) {
    const weapon = character.weapons[i];
    const cw = resolveCombatWeapon(character, weapon.id);
    if (cw) {
      if (!stats) stats = resolveAttackerStats(character, weapon.id);
      resolvedSlots.push({
        ...cw,
        id: resolvedSlots.length,
        weaponId: weapon.id,
        name: '',
      } as unknown as Slot);
    }
  }

  // Fallback: resolve base character stats even when there are no weapons
  // (e.g. for the Overview tab to display WS/BS and talent-derived flags)
  if (!stats) {
    stats = resolveAttackerStats(character, '');
  }

  return { slots: resolvedSlots, stats };
}
